import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography,
  Box, Stack, TextField, Button, Paper, InputAdornment, alpha, useTheme,
  CircularProgress,
} from '@mui/material';
import { Close, MoneyOff } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import letterService from '../../services/letterService';
import { LetterV2 } from '../../types/letters';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useAuth } from '../../context/AuthContext';

interface Props {
  open: boolean;
  onClose: () => void;
  letter: LetterV2 | null;
  onWaived?: () => void;
}

const WaiveFineDialog: React.FC<Props> = ({ open, onClose, letter, onWaived }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const money = useCurrencyFormat();
  const { currency } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const outstanding = useMemo(() => {
    if (!letter || letter.amount == null) return 0;
    return Math.max(0, (letter.amount || 0) - (letter.paidAmount || 0) - (letter.waivedAmount || 0));
  }, [letter]);

  const formik = useFormik({
    initialValues: {
      amount: outstanding ? String(outstanding) : '',
      reason: '',
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      amount: yup.number()
        .typeError('Amount must be a number')
        .positive('Amount must be positive')
        .max(outstanding, `Cannot exceed ${money(outstanding)}`)
        .required('Amount is required'),
      reason: yup.string().required('Reason is required').max(500),
    }),
    onSubmit: async (values) => {
      if (!letter) return;
      setSubmitting(true);
      try {
        await letterService.waiveFine(letter._id, Number(values.amount), values.reason.trim());
        enqueueSnackbar('Fine waived', { variant: 'success' });
        onWaived?.();
        handleClose();
      } catch (err: any) {
        enqueueSnackbar(err.message || 'Failed to waive fine', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (!open) formik.resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    formik.resetForm();
    onClose();
  };

  if (!letter) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.grey[700], 0.1),
            color: theme.palette.grey[700],
          }}>
            <MoneyOff fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>Waive Fine</Typography>
            <Typography variant="caption" color="text.secondary">
              {letter.title || letter.reason}
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={handleClose} disabled={submitting}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Paper elevation={0} sx={{
          p: 2, mb: 2.5, borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.default,
          textAlign: 'center',
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Outstanding balance
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ color: theme.palette.text.primary, mt: 0.5 }}>
            {money(outstanding)}
          </Typography>
        </Paper>

        <form id="waive-fine-form" onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              name="amount"
              label="Amount to waive"
              type="number"
              inputProps={{ min: 0, max: outstanding, step: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
              value={formik.values.amount}
              onChange={formik.handleChange}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && (formik.errors.amount as string)}
            />
            <TextField
              fullWidth
              size="small"
              name="reason"
              label="Reason"
              placeholder="Why is this fine being waived?"
              multiline
              minRows={3}
              value={formik.values.reason}
              onChange={formik.handleChange}
              error={formik.touched.reason && Boolean(formik.errors.reason)}
              helperText={formik.touched.reason && (formik.errors.reason as string)}
            />
          </Stack>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>Cancel</Button>
        <Button
          type="submit"
          form="waive-fine-form"
          variant="contained"
          color="inherit"
          disabled={submitting || outstanding === 0}
          startIcon={submitting ? <CircularProgress size={16} /> : <MoneyOff />}
          sx={{ borderRadius: 2, bgcolor: theme.palette.grey[700], color: 'white', '&:hover': { bgcolor: theme.palette.grey[800] } }}
        >
          {submitting ? 'Waiving…' : 'Waive Fine'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaiveFineDialog;
